<script setup>
/* global axios */
// Chats Internos — mensageria entre agentes (lista + conversa + realtime ActionCable),
// com som de notificação, gravação de áudio e @menções (paridade com o original).
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  defineAsyncComponent,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import AudioChip from 'dashboard/components-next/message/chips/Audio.vue';
import KanbanMultiSelect from './KanbanMultiSelect.vue';

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const myId = computed(() => currentUser.value?.id);
const isAdmin = computed(() => currentRole.value === 'administrator');

const chats = ref([]);
const agents = ref([]);
const agentOptions = computed(() =>
  agents.value.map(a => ({ value: a.id, label: a.name }))
);
const loading = ref(true);
const selected = ref(null);
const sending = ref(false);
const messageText = ref('');
const files = ref([]);
const fileInput = ref(null);
const msgScroll = ref(null);
const inputRef = ref(null);

const showCreate = ref(false);
const createName = ref('');
const createMembers = ref([]);
const showMembers = ref(false);
const memberTick = ref(0);

// som
const soundEnabled = ref(localStorage.getItem('chatSoundEnabled') !== 'false');
let audioCtx = null;
const unlockAudio = () => {
  try {
    if (!audioCtx || audioCtx.state === 'closed') audioCtx = new AudioContext();
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  } catch {
    audioCtx = null;
  }
};
const playSound = () => {
  try {
    if (!audioCtx || audioCtx.state !== 'running') return;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.4);
    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.4);
  } catch {
    /* noop */
  }
};
const toggleSound = () => {
  unlockAudio();
  soundEnabled.value = !soundEnabled.value;
  localStorage.setItem('chatSoundEnabled', String(soundEnabled.value));
};

// gravação de áudio (waveform via AudioRecorder/wavesurfer, igual ao ticket)
const isRecording = ref(false);
const recordingDuration = ref('00:00');
const audioRecorder = ref(null);
const toggleRecording = () => {
  if (isRecording.value) audioRecorder.value?.stopRecording();
  else isRecording.value = true;
};
const onFinishRecord = ({ file }) => {
  if (file) files.value.push(file);
  isRecording.value = false;
  recordingDuration.value = '00:00';
};
const onRecordError = () => {
  isRecording.value = false;
  window.alert(t('CHATS.micError'));
};

const audioAttachment = a => ({
  dataUrl: a.url,
  fileType: a.content_type,
  extension: (a.filename || '').split('.').pop() || 'webm',
});

// emoji
const showEmoji = ref(false);
const addEmoji = emoji => {
  const el = inputRef.value;
  const val = messageText.value;
  const cur = el?.selectionStart ?? val.length;
  messageText.value = val.slice(0, cur) + emoji + val.slice(cur);
  showEmoji.value = false;
  nextTick(() => el?.focus());
};

// @menções
const mentionQuery = ref('');
const showMention = ref(false);
const mentionIndex = ref(0);
const mentionCandidates = computed(() => {
  if (!selected.value) return [];
  const ids = new Set(selected.value.members.map(m => m.user_id));
  return agents.value
    .filter(a => ids.has(a.id) && a.id !== myId.value)
    .filter(
      a =>
        !mentionQuery.value ||
        a.name.toLowerCase().includes(mentionQuery.value.toLowerCase())
    );
});
const onInput = e => {
  const val = e.target.value;
  const cursor = e.target.selectionStart ?? val.length;
  const match = val.substring(0, cursor).match(/@([^\s@]*)$/);
  if (match) {
    mentionQuery.value = match[1];
    showMention.value = true;
    mentionIndex.value = 0;
  } else {
    showMention.value = false;
    mentionQuery.value = '';
  }
};
const insertMention = name => {
  const el = inputRef.value;
  const val = messageText.value;
  const cursor = el?.selectionStart ?? val.length;
  const before = val.substring(0, cursor).replace(/@[^\s@]*$/, `@${name} `);
  messageText.value = before + val.substring(cursor);
  showMention.value = false;
  mentionQuery.value = '';
  nextTick(() => el?.focus());
};
const msgParts = content =>
  String(content || '')
    .split(/(@\S+)/g)
    .map(part => ({ text: part, mention: /^@\S+$/.test(part) }));

const sendLabel = computed(() => {
  const mod = /Mac/i.test(navigator.platform || '') ? '⌘' : 'CTRL';
  return `${t('CHATS.send')} (${mod} + ↵)`;
});

const fmtTime = iso =>
  iso
    ? new Date(iso).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const loadChats = async () => {
  chats.value = (await axios.get(`${base}/internal_chats`)).data.data || [];
};
const loadAgents = async () => {
  try {
    agents.value =
      (await axios.get(`/api/v1/accounts/${accountId}/agents`)).data || [];
  } catch {
    agents.value = [];
  }
};
const scrollDown = async () => {
  await nextTick();
  if (msgScroll.value) msgScroll.value.scrollTop = msgScroll.value.scrollHeight;
};
const selectChat = async chat => {
  selected.value = (
    await axios.get(`${base}/internal_chats/${chat.id}`)
  ).data.data;
  await axios.post(`${base}/internal_chats/${chat.id}/read`).catch(() => {});
  const c = chats.value.find(x => x.id === chat.id);
  if (c) c.unread_count = 0;
  scrollDown();
};

const onPickFiles = e => {
  files.value = [...files.value, ...Array.from(e.target.files || [])].slice(
    0,
    5
  );
  if (fileInput.value) fileInput.value.value = '';
};
const removeFile = i => files.value.splice(i, 1);

const sendMessage = async () => {
  if (!selected.value) return;
  if (!messageText.value.trim() && !files.value.length) return;
  sending.value = true;
  try {
    const fd = new FormData();
    fd.append('content', messageText.value);
    files.value.forEach(f => fd.append('files[]', f));
    const msg = (
      await axios.post(
        `${base}/internal_chats/${selected.value.id}/messages`,
        fd
      )
    ).data.data;
    selected.value.messages.push(msg);
    messageText.value = '';
    files.value = [];
    showMention.value = false;
    await loadChats();
    scrollDown();
  } finally {
    sending.value = false;
  }
};

const onKeydown = e => {
  if (showMention.value && mentionCandidates.value.length) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      mentionIndex.value =
        (mentionIndex.value + 1) % mentionCandidates.value.length;
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      mentionIndex.value =
        (mentionIndex.value - 1 + mentionCandidates.value.length) %
        mentionCandidates.value.length;
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      insertMention(mentionCandidates.value[mentionIndex.value].name);
      return;
    }
    if (e.key === 'Escape') {
      showMention.value = false;
      return;
    }
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
};

const createChat = async () => {
  const ids = createMembers.value;
  if (!createName.value.trim() || !ids.length) return;
  await axios.post(`${base}/internal_chats`, {
    name: createName.value.trim(),
    member_ids: ids,
  });
  showCreate.value = false;
  createName.value = '';
  createMembers.value = [];
  await loadChats();
};

const isMember = id => selected.value?.members?.some(m => m.user_id === id);
const toggleMember = async id => {
  if (!selected.value) return;
  const body = isMember(id)
    ? { remove_user_ids: [id] }
    : { add_user_ids: [id] };
  selected.value = (
    await axios.put(`${base}/internal_chats/${selected.value.id}/members`, body)
  ).data.data;
  memberTick.value += 1;
  await loadChats();
};
const deleteChat = async () => {
  if (!selected.value) return;
  if (!window.confirm(t('CHATS.confirmDelete', { name: selected.value.name })))
    return;
  await axios.delete(`${base}/internal_chats/${selected.value.id}`);
  selected.value = null;
  showMembers.value = false;
  await loadChats();
};

const isImage = a => (a.content_type || '').startsWith('image/');
const isAudio = a => (a.content_type || '').startsWith('audio/');

// Realtime
const onRealtimeMessage = data => {
  if (String(data.account_id) !== String(accountId)) return;
  const { chat_id: chatId, message } = data;
  if (message && message.user_id === myId.value) return;
  if (selected.value && selected.value.id === chatId) {
    selected.value.messages.push(message);
    axios.post(`${base}/internal_chats/${chatId}/read`).catch(() => {});
    scrollDown();
  } else if (soundEnabled.value) {
    playSound();
  }
  loadChats();
};
const onRealtimeUpdated = () => loadChats();

onMounted(async () => {
  await Promise.all([loadChats(), loadAgents()]);
  loading.value = false;
  emitter.on('kanban:internal_chat:message', onRealtimeMessage);
  emitter.on('kanban:internal_chat:updated', onRealtimeUpdated);
});
onBeforeUnmount(() => {
  emitter.off('kanban:internal_chat:message', onRealtimeMessage);
  emitter.off('kanban:internal_chat:updated', onRealtimeUpdated);
});
</script>

<template>
  <div class="flex w-full h-full bg-n-background" @click="unlockAudio">
    <!-- lista -->
    <div class="flex flex-col border-r w-80 border-n-weak bg-n-solid-1">
      <div
        class="flex items-center justify-between px-4 py-4 border-b border-n-weak"
      >
        <div>
          <h1 class="text-heading-1 text-n-slate-12">
            {{ t('CHATS.title') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('CHATS.chatCount', { count: chats.length }) }}
          </p>
        </div>
        <div class="flex gap-1">
          <button
            class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            :title="soundEnabled ? t('CHATS.soundOn') : t('CHATS.soundOff')"
            @click="toggleSound"
          >
            <svg
              v-if="soundEnabled"
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15.536 8.464a5 5 0 010 7.072M12 6l-4 4H4v4h4l4 4V6z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M18.364 5.636a9 9 0 010 12.728"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
              />
            </svg>
          </button>
          <button
            v-if="isAdmin"
            class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            :title="t('CHATS.newChat')"
            @click="showCreate = true"
          >
            <svg
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <p v-if="loading" class="p-4 text-sm text-center text-n-slate-10">
          {{ t('CHATS.loading') }}
        </p>
        <p
          v-else-if="!chats.length"
          class="p-4 text-sm text-center text-n-slate-10"
        >
          {{ isAdmin ? t('CHATS.noChatsAdmin') : t('CHATS.noChatsAgent') }}
        </p>
        <button
          v-for="chat in chats"
          :key="chat.id"
          class="w-full px-4 py-3 text-left border-b border-n-weak hover:bg-n-alpha-1"
          :class="{ 'bg-n-alpha-2': selected && selected.id === chat.id }"
          @click="selectChat(chat)"
        >
          <div class="flex items-center justify-between gap-2">
            <span
              class="font-semibold truncate"
              :class="chat.unread_count ? 'text-n-brand' : 'text-n-slate-12'"
              >{{ chat.name }}</span
            >
            <span
              v-if="chat.unread_count"
              class="flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white rounded-full bg-n-brand"
              >{{ chat.unread_count > 99 ? '99+' : chat.unread_count }}</span
            >
          </div>
          <div class="text-xs text-n-slate-10 mt-0.5">
            {{ t('CHATS.memberCount', { count: chat.members.length }) }}
          </div>
          <div
            v-if="chat.last_message"
            class="mt-1 text-xs truncate text-n-slate-10"
          >
            {{ chat.last_message.content || t('CHATS.attachment') }}
          </div>
        </button>
      </div>
    </div>

    <!-- conversa -->
    <div class="flex flex-col flex-1">
      <template v-if="selected">
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-solid-1"
        >
          <div>
            <h2 class="text-heading-2 text-n-slate-12">
              {{ selected.name }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('CHATS.memberCount', { count: selected.members.length }) }}
            </p>
          </div>
          <div v-if="isAdmin" class="flex gap-1">
            <button
              class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
              :title="t('CHATS.manageMembers')"
              @click="showMembers = true"
            >
              <svg
                class="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z"
                />
              </svg>
            </button>
            <button
              class="p-1.5 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
              :title="t('CHATS.deleteChat')"
              @click="deleteChat"
            >
              <svg
                class="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </div>

        <div ref="msgScroll" class="flex-1 p-4 space-y-3 overflow-y-auto">
          <p
            v-if="!selected.messages.length"
            class="mt-8 text-sm text-center text-n-slate-10"
          >
            {{ t('CHATS.noMessages') }}
          </p>
          <div
            v-for="m in selected.messages"
            :key="m.id"
            class="flex flex-col"
            :class="m.user_id === myId ? 'items-end' : 'items-start'"
          >
            <span class="px-1 mb-1 text-xs font-semibold text-n-slate-10">{{
              m.user_name
            }}</span>
            <div
              class="max-w-lg px-4 py-3 text-sm rounded-xl text-n-slate-12"
              :class="
                m.user_id === myId
                  ? 'bg-n-solid-blue rounded-br-sm'
                  : 'bg-n-slate-4 rounded-bl-sm'
              "
            >
              <div
                v-if="m.content"
                class="text-sm break-words whitespace-pre-wrap"
              >
                <template v-for="(part, pi) in msgParts(m.content)" :key="pi">
                  <span
                    v-if="part.mention"
                    class="font-semibold text-n-blue-11"
                    >{{ part.text }}</span
                  ><template v-else>{{ part.text }}</template>
                </template>
              </div>
              <div
                v-if="m.attachments && m.attachments.length"
                class="mt-2 space-y-2"
              >
                <template v-for="(a, i) in m.attachments" :key="i">
                  <a
                    v-if="isImage(a)"
                    :href="a.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      :src="a.url"
                      :alt="a.filename"
                      class="rounded cursor-pointer max-w-xs hover:opacity-90"
                    />
                  </a>
                  <AudioChip
                    v-else-if="isAudio(a)"
                    :attachment="audioAttachment(a)"
                    :show-transcribed-text="false"
                    class="w-72 max-w-full"
                  />
                  <a
                    v-else
                    :href="a.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-2 px-2 py-1 text-xs underline rounded bg-n-alpha-2"
                    >{{ a.filename }}</a
                  >
                </template>
              </div>
              <div class="flex justify-end mt-2 text-xs text-n-slate-11">
                {{ fmtTime(m.created_at) }}
              </div>
            </div>
          </div>
        </div>

        <!-- input (mesmo formato da caixa de resposta do ticket) -->
        <div
          class="relative mx-2 mb-2 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div
            v-if="showMention && mentionCandidates.length"
            class="absolute left-3 right-3 z-10 mb-1 overflow-y-auto border rounded-lg shadow-lg bottom-full max-h-40 bg-n-solid-2 border-n-weak"
          >
            <button
              v-for="(a, i) in mentionCandidates"
              :key="a.id"
              class="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-1"
              :class="{ 'bg-n-alpha-2': i === mentionIndex }"
              @mousedown.prevent="insertMention(a.name)"
            >
              <span class="font-semibold text-n-brand">{{ '@' }}</span
              >{{ a.name }}
            </button>
          </div>

          <AudioRecorder
            v-if="isRecording"
            ref="audioRecorder"
            audio-record-format="audio/wav"
            class="px-2 pt-2"
            @finish-record="onFinishRecord"
            @recorder-progress-changed="d => (recordingDuration = d)"
            @record-error="onRecordError"
          />
          <textarea
            v-else
            ref="inputRef"
            v-model="messageText"
            rows="3"
            :placeholder="t('CHATS.messagePlaceholder')"
            class="block w-full px-3 pt-3 text-sm resize-none text-n-slate-12 placeholder:text-n-slate-10 !bg-transparent !border-0 !outline-0 !mb-0 !h-auto !rounded-none !shadow-none focus:!outline-0 focus:!ring-0"
            @input="onInput"
            @keydown="onKeydown"
          />

          <div v-if="files.length" class="flex flex-wrap gap-2 px-3 pb-1">
            <span
              v-for="(f, i) in files"
              :key="i"
              class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ f.name }}
              <button class="text-n-ruby-11" @click="removeFile(i)">
                {{ '×' }}
              </button>
            </span>
          </div>

          <input
            ref="fileInput"
            type="file"
            multiple
            accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
            class="hidden"
            @change="onPickFiles"
          />

          <div
            v-if="showEmoji"
            class="fixed inset-0 z-10"
            @click="showEmoji = false"
          />
          <EmojiIconPicker
            v-if="showEmoji"
            mode="emoji"
            class="!absolute z-20 left-2 bottom-14"
            @select="addEmoji($event.value)"
          />

          <div class="flex justify-between p-3">
            <div class="flex items-center gap-2">
              <NextButton
                v-tooltip.top-end="t('CHATS.emoji')"
                icon="i-ph-smiley-sticker"
                slate
                faded
                sm
                @click="showEmoji = !showEmoji"
              />
              <NextButton
                v-tooltip.top-end="t('CHATS.attach')"
                icon="i-ph-paperclip"
                slate
                faded
                sm
                @click="fileInput.click()"
              />
              <NextButton
                v-tooltip.top-end="
                  isRecording
                    ? t('CHATS.stopRecording')
                    : t('CHATS.startRecording')
                "
                :icon="
                  isRecording ? 'i-ph-microphone-slash' : 'i-ph-microphone'
                "
                :color="isRecording ? 'ruby' : 'slate'"
                faded
                sm
                @click="toggleRecording"
              />
              <NextButton
                v-if="isRecording"
                v-tooltip.top-end="t('CHATS.stopRecording')"
                icon="i-lucide-square"
                :label="recordingDuration"
                ruby
                faded
                sm
                @click="toggleRecording"
              />
            </div>
            <div class="flex">
              <NextButton
                :label="sendLabel"
                blue
                sm
                class="flex-shrink-0"
                :disabled="sending || (!messageText.trim() && !files.length)"
                @click="sendMessage"
              />
            </div>
          </div>
        </div>
      </template>
      <div
        v-else
        class="flex items-center justify-center flex-1 text-sm text-n-slate-10"
      >
        {{ t('CHATS.selectPrompt') }}
      </div>
    </div>

    <!-- modal criar chat -->
    <div
      v-if="showCreate"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showCreate = false"
    >
      <div class="w-full max-w-md shadow-md rounded-xl bg-n-solid-2">
        <div class="px-6 py-4 border-b border-n-weak">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('CHATS.newChat') }}
          </h3>
        </div>
        <div class="px-6 py-5 space-y-3">
          <input
            v-model="createName"
            :placeholder="t('CHATS.chatNamePlaceholder')"
            class="block w-full px-3 py-2 text-sm border-0 rounded-lg outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 outline-n-weak focus:outline-n-brand focus:ring-0"
          />
          <div>
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ t('CHATS.selectMembers') }}
            </p>
            <KanbanMultiSelect
              v-model="createMembers"
              :options="agentOptions"
              align="top"
              :placeholder="t('CHATS.selectMembers')"
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 px-6 py-4 border-t border-n-weak">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
            @click="showCreate = false"
          >
            {{ t('CHATS.cancel') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110 disabled:opacity-60"
            :disabled="!createName.trim() || !createMembers.length"
            @click="createChat"
          >
            {{ t('CHATS.create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- modal membros -->
    <div
      v-if="showMembers && selected"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showMembers = false"
    >
      <div
        class="w-full max-w-md overflow-hidden shadow-md rounded-xl bg-n-solid-2"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('CHATS.manageMembers') }}
          </h3>
          <button
            class="p-1 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="showMembers = false"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="px-6 py-5 overflow-y-auto max-h-[60vh]">
          <label
            v-for="a in agents"
            :key="a.id"
            class="flex items-center gap-2 px-1 py-2 text-sm cursor-pointer text-n-slate-11"
          >
            <input
              type="checkbox"
              :checked="memberTick >= 0 && isMember(a.id)"
              @change="toggleMember(a.id)"
            />
            {{ a.name }}
          </label>
        </div>
        <div class="flex justify-end px-6 py-4 border-t border-n-weak">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
            @click="showMembers = false"
          >
            {{ t('CHATS.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
